/*
 * CYBERFEST schedule helper (snippets/cf-boot.liquid sets window.cfCampaign).
 * - Pages are cached for guests, so a page rendered just before a 09:00 GST switch can be served a few
 *   minutes after it. When the next campaign boundary has passed, reload (never while the shopper
 *   is typing or has the cart drawer open; never in preview mode) and verify the new render advanced, with
 *   bounded retries (see maybeReload).
 * - Elements with data-cf-until (Unix seconds) hide themselves once that time passes.
 * - <cf-countdown data-cf-target="unix-seconds"> shows days / hours / minutes / seconds to a time.
 */
(() => {
  const cfg = window.cfCampaign;
  if (!cfg) return;

  // In preview the page pretends it is cfg.now; keep the same offset so countdowns run from there.
  const offset = cfg.preview ? cfg.now - Math.floor(Date.now() / 1000) : 0;
  const nowSeconds = () => Math.floor(Date.now() / 1000) + offset;
  window.cfCampaign.nowSeconds = nowSeconds;

  const hideExpired = (root = document) => {
    const t = nowSeconds();
    root.querySelectorAll('[data-cf-until]').forEach((el) => {
      const until = Number(el.getAttribute('data-cf-until'));
      if (until > 0 && t >= until && !el.hasAttribute('data-cf-keep')) el.hidden = true;
    });
  };

  const openDrawer = () => document.querySelector('cart-drawer.active, .hb-cart-drawer[open], .hb-cart-drawer.is-open');
  let drawerNext = Number(cfg.next) || 0;
  let drawerRefresh = null;
  let drawerRetryAt = 0;
  const refreshOpenDrawer = () => {
    if (!drawerNext || nowSeconds() < drawerNext || drawerRefresh || Date.now() < drawerRetryAt) return;
    const engine = window.hbCartEngine;
    if (typeof engine?.reconcile !== 'function') return;
    drawerRetryAt = Date.now() + 60000;
    drawerRefresh = Promise.resolve(engine.reconcile('cf-boundary', { refresh: true, boundary: drawerNext }))
      .then((refreshed) => {
        if (!refreshed) return;
        const fresh = window.cfCart?.readConfig?.();
        if (fresh && Number(fresh.renderedFor) < drawerNext) return;
        drawerNext = fresh ? Number(fresh.next) || 0 : 0;
      })
      .catch(() => {})
      .finally(() => { drawerRefresh = null; });
  };

  const busy = () => {
    const a = document.activeElement;
    if (a && (a.matches('input, textarea, select') || a.isContentEditable)) return true;
    if (document.querySelector('cart-drawer.active, .hb-cart-drawer[open], .hb-cart-drawer.is-open, dialog[open]')) return true;
    return false;
  };

  // Boundary refresh with verification. A reload is only counted as done once the re-rendered page reports a
  // later boundary (window.cfCampaign.next after the load is past the stored one). If the response was still the
  // stale cached page (same boundary), retry with backoff: try 1 at once (plain reload), try 2 after 60 s and try 3
  // after another 180 s (both with a cache-busting _cfr query parameter, removed again once the render advanced).
  // After 3 tries we pause for 30 minutes (a stale cache entry is gone by then) and then start a fresh cycle of 3.
  // So a page that stays stale makes at most 3 reloads per 30 minutes (never a loop), and recovery is never
  // suppressed for good (the spec's "stop until the next boundary" is relaxed on purpose).
  // Client clock ahead of the server: a genuinely new render (cfg.renderedAt differs from the stale one) that still
  // sits before the boundary means the server has not reached it yet. That is not a stale cache, so the try is not
  // counted and we wait roughly the real remaining time (b - renderedAt) before looking again.
  const RETRY_WAIT_MS = [0, 60000, 180000];
  const COOLDOWN_MS = 30 * 60 * 1000;
  const MAX_SKEW_HOLDS = 5;
  const stateKey = `cf-sched:${location.pathname}`;
  const readState = () => {
    try {
      return JSON.parse(sessionStorage.getItem(stateKey) || 'null');
    } catch (e) {
      return null;
    }
  };
  const writeState = (state) => {
    try {
      if (state) sessionStorage.setItem(stateKey, JSON.stringify(state));
      else sessionStorage.removeItem(stateKey);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Runs once per page load: did the reload we triggered land on a render that moved past the stored boundary?
  const reconcileReload = () => {
    let state = readState();
    let advanced = false;
    if (state && (!cfg.next || cfg.next > state.b)) {
      writeState(null);
      advanced = true;
      state = null;
    } else if (
      state &&
      state.n > 0 &&
      (state.k || 0) < MAX_SKEW_HOLDS &&
      cfg.renderedAt &&
      state.r &&
      cfg.renderedAt > state.r &&
      cfg.renderedAt < state.b
    ) {
      // New render, server still before the boundary: our clock is ahead. Do not count the try; hold until about then.
      const waitSeconds = Math.min(state.b - cfg.renderedAt + 2, COOLDOWN_MS / 1000);
      writeState({ b: state.b, n: state.n - 1, t: state.t, r: cfg.renderedAt, k: (state.k || 0) + 1, hold: Date.now() + waitSeconds * 1000 });
    }
    try {
      const url = new URL(location.href);
      if (url.searchParams.has('_cfr') && (!state || advanced)) {
        url.searchParams.delete('_cfr');
        history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      }
    } catch (e) {
      /* ignore */
    }
  };

  const maybeReload = () => {
    if (cfg.preview || !cfg.next) return;
    if (document.visibilityState !== 'visible') return;
    if (openDrawer()) {
      refreshOpenDrawer();
      return;
    }
    if (nowSeconds() < cfg.next || busy()) return;
    const stamp = Date.now();
    let state = readState();
    // A render older than the stored boundary keeps the stored counter (no fresh cycle, so no ping-pong between two stale entries).
    if (!state || cfg.next > state.b) state = { b: cfg.next, n: 0, t: 0, r: 0, k: 0 };
    if (state.hold && stamp < state.hold) return;
    if (state.n >= RETRY_WAIT_MS.length) {
      if (stamp - state.t < COOLDOWN_MS) return;
      state = { b: state.b, n: 0, t: 0, r: 0, k: 0 };
    }
    if (state.n > 0 && stamp - state.t < RETRY_WAIT_MS[state.n]) return;
    const attempt = state.n;
    // Without sessionStorage we cannot tell a stale response from a fresh one, so do not risk a reload loop.
    if (!writeState({ b: state.b, n: attempt + 1, t: stamp, r: cfg.renderedAt || 0, k: state.k || 0 })) return;
    if (attempt === 0) {
      location.reload();
    } else {
      const url = new URL(location.href);
      url.searchParams.set('_cfr', String(stamp));
      location.replace(url.toString());
    }
  };

  class CfCountdown extends HTMLElement {
    connectedCallback() {
      this.target = Number(this.dataset.cfTarget || 0);
      if (!this.target) return;
      this.render();
      this.timer = setInterval(() => this.render(), 1000);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
    render() {
      let left = Math.max(0, this.target - nowSeconds());
      if (left === 0) {
        clearInterval(this.timer);
        if (this.dataset.cfExpired === 'hide') {
          (this.closest('[data-cf-countdown-wrap]') || this).hidden = true;
          return;
        }
      }
      const d = Math.floor(left / 86400);
      left -= d * 86400;
      const h = Math.floor(left / 3600);
      left -= h * 3600;
      const m = Math.floor(left / 60);
      const s = left - m * 60;
      const parts = { days: d, hours: h, minutes: m, seconds: s };
      const s2 = cfg.strings || {};
      let html = '';
      ['days', 'hours', 'minutes', 'seconds'].forEach((unit) => {
        if (unit === 'days' && d === 0 && this.dataset.cfHideZeroDays !== 'false') return;
        const n = String(parts[unit]).padStart(2, '0');
        html += `<span class="cf-countdown__unit"><span class="cf-countdown__num">${n}</span><span class="cf-countdown__label">${s2[unit] || unit}</span></span>`;
      });
      if (this.innerHTML !== html) this.innerHTML = html;
      if (window.hbNumerals && typeof window.hbNumerals.convert === 'function') window.hbNumerals.convert(this);
    }
  }
  if (!customElements.get('cf-countdown')) customElements.define('cf-countdown', CfCountdown);

  const init = () => {
    hideExpired();
    reconcileReload();
    maybeReload();
    setInterval(() => {
      hideExpired();
      maybeReload();
    }, 15000);
    document.addEventListener('visibilitychange', maybeReload);
    document.addEventListener('focusout', () => setTimeout(maybeReload, 500));
    document.addEventListener('shopify:section:load', (e) => hideExpired(e.target));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
